import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { AccountRoutesConfig } from 'modules/account';
import { useChainData } from 'modules/common';
import { ReactComponent as WarningIcon } from 'modules/common/assets/warning.svg';
import { useTranslation } from 'modules/i18n';

import translation from './balance.translation';

interface Props {
  balance: BigNumber;
  isLow: boolean;
}

export function Balance({ balance, isLow }: Props): JSX.Element {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(translation);
  const chainData = useChainData();

  return (
    <Link
      to={AccountRoutesConfig.account.generatePath()}
      className={cx(classes.tag, classes.activeTag)}
    >
      <Typography
        variant="body4"
        sx={theme => ({ color: theme.palette.grey[600] })}
      >
        <span className={classes.description}>
          {t(keys.funds)}
          &nbsp;
        </span>
        <Box
          component="span"
          sx={theme => ({
            color: isLow
              ? theme.palette.error.main
              : theme.palette.primary.main,
          })}
        >
          {t(keys.tokenAmount, {
            amount: balance.decimalPlaces(4).toString(),
            token: chainData?.tokenName,
          })}
        </Box>
      </Typography>
      {isLow && <WarningIcon className={classes.icon} />}
    </Link>
  );
}

const useStyles = makeStyles()(theme => ({
  tag: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2.5),
    height: 40,
    borderRadius: 12,
    padding: theme.spacing(2, 4),
    backgroundColor: theme.palette.common.white,

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2.5, 2),
    },
  },

  activeTag: {
    cursor: 'pointer',
    textDecoration: 'none',
    transition: '0.3s',
    width: 'max-content',

    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },

  icon: {
    color: theme.palette.error.main,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  description: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
}));
